import type { DragEvent as ReactDragEvent } from "react";
import type { ChatDraftKind } from "../context/chatAttachmentTypes";
import { inferChatDraftKind } from "../context/chatAttachmentTypes";

export type ChatDropZone = "media" | "doc";

const IMAGE_EXT_RE = /\.(jpe?g|png|gif|webp|bmp|heic|heif)$/i;
const VIDEO_EXT_RE = /\.(mp4|mov|m4v|webm|avi|mkv)$/i;

export function isFileDragEvent(e: DragEvent | ReactDragEvent): boolean {
  const types = e.dataTransfer?.types;
  if (!types) return false;
  return Array.from(types as unknown as string[]).includes("Files");
}

export function isPreviewableImageFile(file: File): boolean {
  const t = file.type || "";
  if (t.startsWith("image/")) return true;
  return IMAGE_EXT_RE.test(file.name || "");
}

export function isPreviewableImagePath(
  href?: string | null,
  fileName?: string | null,
): boolean {
  const name = String(fileName || "");
  if (IMAGE_EXT_RE.test(name)) return true;
  const path = String(href || "").split(/[?#]/)[0] || "";
  return IMAGE_EXT_RE.test(path);
}

/** Drop as photo/video when zone=media; force doc (original) when zone=doc. */
export function resolveDropKind(
  file: File,
  zone: ChatDropZone,
): ChatDraftKind | null {
  if (zone === "doc") {
    const inferred = inferChatDraftKind(file);
    if (inferred) return "doc";
    // Allow any image/video as uncompressed file even if mime empty
    if (isPreviewableImageFile(file) || VIDEO_EXT_RE.test(file.name || "")) {
      return "doc";
    }
    // TEMP: mp3/audio drop as file → server turns into voice
    if (
      (file.type || "").startsWith("audio/") ||
      /\.(mp3|m4a|aac|ogg|wav|flac|opus)$/i.test(file.name || "")
    ) {
      return "doc";
    }
    return null;
  }
  return inferChatDraftKind(file);
}

export function dropHasMediaFiles(files: File[]): boolean {
  return files.some((f) => {
    const k = inferChatDraftKind(f);
    return k === "image" || k === "video";
  });
}

/** Prefer DataTransferItemList types (available on dragenter before drop). */
export function dragPayloadLooksLikeMedia(
  dt: DataTransfer | null | undefined,
): boolean {
  if (!dt) return true;
  const files = Array.from(dt.files || []);
  if (files.length > 0) return dropHasMediaFiles(files);

  const items = Array.from(dt.items || []);
  if (items.length === 0) return true; // unknown → show photo/file split
  let sawFile = false;
  let sawMedia = false;
  for (const it of items) {
    if (it.kind !== "file") continue;
    sawFile = true;
    const t = String(it.type || "");
    if (t.startsWith("image/") || t.startsWith("video/")) sawMedia = true;
  }
  if (!sawFile) return true;
  return sawMedia;
}
